<template>
  <div class="custom-datepicker">
    <div class="custom-datepicker__title text-body-1 text--text mb-2">
      {{ title }}
    </div>

    <div class="d-flex justify-space-between mb-2">
      <div
        v-for="dayName of daysNames"
        :key="dayName"
        class="custom-datepicker__day-wrapper"
      >
        <div class="custom-datepicker__day text-body-2 text-secondary--text">
          {{ $i18next.t(dayName, options) }}
        </div>
      </div>
    </div>
    <div
      v-for="(week, index) of weeks"
      :key="`${week[0].value}-${index}`"
      class="d-flex justify-space-between"
    >
      <template v-for="day of week">
        <div
          :key="`${day.value}-${index}`"
          class="custom-datepicker__day-wrapper"
          :class="{ 'custom-datepicker__day_disabled': !day.enabled }"
          @click="onSelect(day)"
        >
          <div
            v-ripple
            class="custom-datepicker__day text-body-2"
            :class="{
              'custom-datepicker__day_current': isToday(day) && showCurrentDay,
              'custom-datepicker__day_selected': isSelectedDay(day),
            }"
          >
            {{ day.dayOfMonth }}
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import {
  isAfter,
  isBefore,
  format,
  formatISO,
  parseISO,
  getDate,
  getMonth,
  addDays,
  startOfWeek,
  differenceInCalendarDays,
} from 'date-fns'
import { ru } from 'date-fns/locale'

export default {
  name: 'CustomDatepicker',
  model: {
    prop: 'value',
    event: 'change',
  },
  props: {
    weeksNum: {
      type: Number,
      default: 2,
    },
    value: {
      type: [String, Array],
      default: '',
    },
    interval: Boolean,
    showCurrentDay: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      weeks: [],
      title: '',
      options: { ns: this.$i18next.language },
    }
  },
  created() {
    this.today = new Date()
    this.daysNames = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс']
    this.initDays()
  },
  methods: {
    initTitle(lastEnabledDay) {
      if (getMonth(this.today) === getMonth(lastEnabledDay)) {
        this.title = format(this.today, 'LLLL', { locale: ru })

        return
      }

      const thisMonth = format(this.today, 'LLLL', { locale: ru })
      const lastMonth = format(lastEnabledDay, 'LLLL', { locale: ru })

      this.title = `${this.$i18next.t(thisMonth, this.options)} – ${this.$i18next.t(lastMonth, this.options)}`
    },
    initDays() {
      const firstDay = startOfWeek(this.today, { weekStartsOn: 1 })
      const enabledDaysNum = this.weeksNum * 7

      let daysNum = enabledDaysNum

      if (differenceInCalendarDays(firstDay, this.today)) {
        daysNum = (this.weeksNum + 1) * 7
      }

      const lastEnabledDay = addDays(this.today, enabledDaysNum - 1)
      let days = []

      for (let i = 0; i < daysNum; i++) {
        if (days.length === 7) {
          this.weeks.push(days)
          days = []
        }

        const date = addDays(firstDay, i)
        const enabled = differenceInCalendarDays(date, this.today) >= 0
                    && differenceInCalendarDays(date, lastEnabledDay) <= 0

        days.push({
          dayOfMonth: getDate(date),
          enabled,
          value: date,
        })
      }

      this.weeks.push(days)

      this.initTitle(lastEnabledDay)
    },
    isToday(day) {
      return this.isSameDay(day.value, this.today)
    },
    isBetweenDates(date, firstDay, lastDay) {
      return (isAfter(date, firstDay) && isBefore(date, lastDay))
                || (isAfter(date, lastDay) && isBefore(date, firstDay))
    },
    isSameDay(date, compDate) {
      return date && compDate && !differenceInCalendarDays(date, compDate)
    },
    isSelectedDay(day) {
      if (!this.value) {
        return
      }

      if (this.interval) {
        const [firstDay, lastDay] = this.value.map(parseISO)

        return this.isSameDay(day.value, firstDay)
                    || this.isSameDay(day.value, lastDay)
                    || this.isBetweenDates(day.value, firstDay, lastDay)
      }

      return this.isSameDay(day.value, parseISO(this.value))
    },
    onSelect(day) {
      if (!day.enabled) {
        return
      }

      const formatDate = date => formatISO(date, { representation: 'date' })

      if (this.interval) {
        const daysSelected = this.value.length
        let innerValue = [...this.value]

        if (!daysSelected || daysSelected === 2) {
          innerValue = []
        }

        innerValue.push(formatDate(day.value))

        this.$emit('change', innerValue.sort())

        return
      }

      this.$emit('change', formatDate(day.value))
    },
  },
}
</script>

<style lang="scss" scoped>
@import '~www/themes/doctors/common/variables';

.custom-datepicker {
  &__title {
    text-transform: capitalize;
    text-align: center;
  }

  &__day {
    width: 36px;
    height: 36px;
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: 24px;
    color: $ui-kit-text;

    &_current {
      color: $ui-kit-primary;
      border: 1px solid currentColor;
    }

    &_selected {
      background: $ui-kit-primary;
      border: 1px solid $ui-kit-primary;
      color: $ui-kit-bg-gray-0;
    }

    &_disabled {
      visibility: hidden;
    }
  }

  &__day-wrapper {
    height: 40px;
    width: 40px;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
  }
}
</style>
